import { useState } from 'react';
import { Check, Delete } from 'lucide-react';
import { useTranslation } from 'react-i18next';

/** Ô hiển thị theo mẫu ("dd/mm/yyyy", "dd/mm"); ô chưa nhập hiện chữ mờ, ô đang nhập gạch chân */
const Slots = ({ digits, mask }: { digits: string; mask: string }) => {
  let i = 0;
  return (
    <>
      {mask.split('').map((ch, k) => {
        if (ch === '/') return <span key={k} className="text-gray-300 mx-0.5">/</span>;
        const filled = digits[i];
        const active = i === digits.length;
        i += 1;
        return (
          <span key={k} className={`inline-block w-[0.7em] text-center ${filled ? 'text-spa-dark' : 'text-gray-300'} ${active ? 'border-b-2 border-spa-gold' : 'border-b-2 border-transparent'}`}>
            {filled || ch}
          </span>
        );
      })}
    </>
  );
};

interface Props {
  title: string;
  hint: string;
  /** Mẫu hiển thị, số ký tự khác "/" là số chữ số cần nhập */
  mask: string;
  /** Chữ số có sẵn khi mở (giá trị đang chọn) */
  initialDigits?: string;
  /** Đủ chữ số và hợp lệ thì trả về "YYYY-MM-DD", không thì null */
  parse: (digits: string) => string | null;
  /** Dòng xác nhận dưới ô khi ngày hợp lệ (tuổi, thứ trong tuần...) */
  describe: (ymd: string) => string;
  invalidText: string;
  onDone: (ymd: string) => void;
  /** Có thì hiện nút xóa giá trị đang chọn */
  onClear?: () => void;
  clearText?: string;
  onClose: () => void;
}

/**
 * Hộp nhập ngày bằng bàn số cho kiosk: khách gõ liền các chữ số, dấu "/" tự chèn.
 * Gõ 4–9 ở ô ngày hoặc 2–9 ở ô tháng thì chắc chắn là một chữ số nên tự thêm số 0 phía trước.
 */
export default function DatePadDialog({ title, hint, mask, initialDigits = '', parse, describe, invalidText, onDone, onClear, clearText, onClose }: Props) {
  const { t } = useTranslation();
  const length = mask.replace(/\//g, '').length;
  const [digits, setDigits] = useState(initialDigits.slice(0, length));

  const parsed = parse(digits);
  const invalid = digits.length === length && !parsed;

  const press = (d: string) => setDigits(prev => {
    if (prev.length >= length) return prev;
    if ((prev.length === 0 && d > '3') || (prev.length === 2 && d > '1')) return `${prev}0${d}`;
    return prev + d;
  });

  const keyCls = 'h-14 md:h-16 bg-white border border-gray-100 rounded-xl text-2xl font-medium text-gray-700 shadow-sm hover:bg-gray-50 active:bg-gray-200 active:scale-95 transition-all';

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center fade-in">
      <div className="absolute inset-0 bg-[#092C23]/70 backdrop-blur-sm" onClick={onClose} />
      <div role="dialog" aria-label={title}
        className="bg-white rounded-[24px] shadow-2xl w-11/12 max-w-sm md:max-w-md relative z-10 p-5 md:p-6 border border-gray-100">
        <h3 className="text-lg font-bold text-gray-800 text-center">{title}</h3>
        <p className="text-sm text-gray-500 text-center mt-1">{hint}</p>

        <div className={`mt-4 rounded-xl border px-4 py-3 text-center text-3xl md:text-4xl font-bold tracking-wide tabular-nums bg-gray-50 shadow-inner ${invalid ? 'border-red-300' : 'border-gray-200'}`}>
          <Slots digits={digits} mask={mask} />
        </div>
        <div className="min-h-6 mt-1.5 text-center text-sm">
          {invalid && <span className="text-red-500">{invalidText}</span>}
          {parsed && <span className="text-emerald-600 font-medium">{describe(parsed)}</span>}
        </div>

        <div className="grid grid-cols-3 gap-2 md:gap-3 mt-2">
          {['1', '2', '3', '4', '5', '6', '7', '8', '9'].map(n => (
            <button key={n} type="button" onClick={() => press(n)} className={keyCls}>{n}</button>
          ))}
          <button type="button" onClick={() => setDigits(prev => prev.slice(0, -1))} aria-label={t('checkin.datepad.backspace')}
            className="h-14 md:h-16 bg-red-50 border border-red-100 rounded-xl text-red-500 shadow-sm hover:bg-red-100 active:bg-red-200 active:scale-95 transition-all flex items-center justify-center">
            <Delete size={24} />
          </button>
          <button type="button" onClick={() => press('0')} className={keyCls}>0</button>
          <button type="button" onClick={() => parsed && onDone(parsed)} disabled={!parsed} aria-label={t('checkin.datepad.done')}
            className="h-14 md:h-16 bg-spa-dark border border-spa-dark rounded-xl text-white shadow-md hover:bg-slate-800 active:scale-95 transition-all flex items-center justify-center disabled:opacity-40 disabled:cursor-not-allowed">
            <Check size={28} />
          </button>
        </div>

        <div className="flex gap-3 mt-4">
          {onClear && (
            <button type="button" onClick={onClear}
              className="flex-1 py-3 rounded-xl border border-gray-200 text-gray-600 text-sm font-medium hover:bg-gray-50 transition-colors">
              {clearText}
            </button>
          )}
          <button type="button" onClick={onClose}
            className="flex-1 py-3 rounded-xl bg-gray-100 text-gray-700 text-sm font-medium hover:bg-gray-200 transition-colors">
            {t('checkin.modal.cancel')}
          </button>
        </div>
      </div>
    </div>
  );
}
